/**
 * MembershipPickerModal — picker for adding a firm to a user (10.4) or a
 * user to a firm (10.5). Search input + result list; selecting a row
 * commits via the provided onSelect callback.
 *
 * `kind === 'company'` lists firms (used inside the User edit modal);
 * `kind === 'user'` lists users (used inside the Company edit modal).
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { Modal, DialogCrown } from '@/components/Modal';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import {
  useCompaniesListQuery,
  useUsersListQuery,
  type MgmtCompany,
  type MgmtUser,
} from './api';

interface CompanyPickerProps {
  open: boolean;
  kind: 'company';
  excludeIds: number[];
  onOpenChange: (open: boolean) => void;
  onSelect: (company: MgmtCompany) => void;
}

interface UserPickerProps {
  open: boolean;
  kind: 'user';
  excludeIds: number[];
  onOpenChange: (open: boolean) => void;
  onSelect: (user: MgmtUser) => void;
}

type MembershipPickerModalProps = CompanyPickerProps | UserPickerProps;

export function MembershipPickerModal(props: MembershipPickerModalProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);

  return (
    <Modal open={props.open} onOpenChange={props.onOpenChange} maxWidth={560}>
      <DialogCrown
        tone="neutral"
        eyebrow={props.kind === 'company' ? 'Přiřadit firmu' : 'Přiřadit uživatele'}
        title={
          props.kind === 'company' ? (
            <>
              Vybrat <em>firmu</em>.
            </>
          ) : (
            <>
              Vybrat <em>uživatele</em>.
            </>
          )
        }
      />
      <Modal.Body className="px-8 py-6">
        <SearchInput
          autoFocus
          kbd={null}
          placeholder={
            props.kind === 'company' ? 'Hledat firmu…' : 'Hledat uživatele…'
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <div className="mt-4 max-h-[320px] overflow-y-auto border border-line bg-paper">
          {props.kind === 'company' ? (
            <CompanyResults
              search={debouncedSearch.trim()}
              excludeIds={props.excludeIds}
              onSelect={(c) => {
                props.onSelect(c);
                props.onOpenChange(false);
              }}
            />
          ) : (
            <UserResults
              search={debouncedSearch.trim()}
              excludeIds={props.excludeIds}
              onSelect={(u) => {
                props.onSelect(u);
                props.onOpenChange(false);
              }}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Foot
        meta="Esc · zavřít"
        actions={
          <Button variant="secondary" size="md" onClick={() => props.onOpenChange(false)}>
            Zrušit
          </Button>
        }
      />
    </Modal>
  );
}

interface CompanyResultsProps {
  search: string;
  excludeIds: number[];
  onSelect: (c: MgmtCompany) => void;
}

function CompanyResults({ search, excludeIds, onSelect }: CompanyResultsProps) {
  const list = useCompaniesListQuery({ search: search || undefined, limit: 50 });
  const rows = (list.data?.companies ?? []).filter((c) => !excludeIds.includes(c.id_firmy));

  if (list.isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        {search ? 'Nic neodpovídá hledání.' : 'Žádná dostupná firma.'}
      </p>
    );
  }
  return (
    <ul className="divide-y divide-line">
      {rows.map((c) => (
        <li key={c.id_firmy}>
          <button
            type="button"
            onClick={() => onSelect(c)}
            className="flex w-full items-baseline gap-3 px-4 py-2.5 text-left text-[12.5px] hover:bg-surface-2"
          >
            <span className="font-mono text-[11px] tabular-nums text-ink-3">
              #{c.id_firmy}
            </span>
            <span className="truncate text-ink">{c.Nazev}</span>
            <span className="ml-auto font-mono text-[11px] text-ink-3">
              {c.mesto || '—'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

interface UserResultsProps {
  search: string;
  excludeIds: number[];
  onSelect: (u: MgmtUser) => void;
}

function UserResults({ search, excludeIds, onSelect }: UserResultsProps) {
  const list = useUsersListQuery({ search: search || undefined, limit: 50 });
  const rows = (list.data?.users ?? []).filter((u) => !excludeIds.includes(u.id_uzivatele));

  if (list.isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        {search ? 'Nic neodpovídá hledání.' : 'Žádný dostupný uživatel.'}
      </p>
    );
  }
  return (
    <ul className="divide-y divide-line">
      {rows.map((u) => (
        <li key={u.id_uzivatele}>
          <button
            type="button"
            onClick={() => onSelect(u)}
            className="flex w-full items-baseline gap-3 px-4 py-2.5 text-left text-[12.5px] hover:bg-surface-2"
          >
            <span className="font-mono text-[11px] tabular-nums text-ink-3">
              #{u.id_uzivatele}
            </span>
            <span className="font-mono text-[12px] text-ink">{u.nick}</span>
            <span className="truncate text-ink-2">
              {[u.jmeno, u.prijmeni].filter(Boolean).join(' ') || '—'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
